import { createFileRoute } from "@tanstack/react-router";
import { alternateLinkList } from "@/lib/i18n/locale";
import { LegalPage, P, UL } from "@/components/marketing/LegalPage";

export const Route = createFileRoute("/vi/legal/returns")({
  head: () => ({
    meta: [
      { title: "Chính sách Đổi trả, Hoàn tiền & Bảo hành — Banana Cards" },
      { name: "description", content: "Cách đổi trả, hoàn tiền, thay thế và bảo hành NFC hoạt động cho sản phẩm vật lý, gói đăng ký và thẻ Google Review của Banana Cards." },
      { property: "og:title", content: "Chính sách Đổi trả, Hoàn tiền & Bảo hành — Banana Cards" },
      { property: "og:description", content: "Thời hạn đổi trả, xử lý sản phẩm lỗi, khắc phục NFC và hoàn tiền gói đăng ký tại Banana Cards." },
    ],
    links: alternateLinkList("/legal/returns", "vi"),
  }),
  component: ReturnsPageVi,
});

function ReturnsPageVi() {
  return (
    <LegalPage
      eyebrow="Pháp lý · Tin cậy"
      title="Chính sách Đổi trả, Hoàn tiền &"
      italic="Bảo hành."
      updated="Tháng 1, 2026"
      intro="Mỗi đơn hàng tại Banana Cards đều được chuẩn bị, mã hóa và kiểm tra kỹ trước khi giao. Vì nhiều sản phẩm của chúng tôi được cá nhân hóa, in ấn, mã hóa hoặc liên kết với hồ sơ khách hàng, nên chính sách đổi trả phụ thuộc vào loại sản phẩm bạn đã mua. Chính sách này giải thích rõ cách đổi trả, hoàn tiền, thay thế và bảo hành được xử lý."
      related={[
        { label: "Điều khoản dịch vụ", to: "/vi/terms" },
        { label: "Chính sách bảo mật", to: "/vi/privacy" },
        { label: "Liên hệ hỗ trợ", to: "/vi/contact" },
      ]}
      sections={[
        {
          id: "non-custom",
          title: "1. Đổi trả sản phẩm không tùy chỉnh",
          body: (
            <>
              <P>Các sản phẩm Banana Cards không tùy chỉnh có thể được trả lại trong vòng 7 ngày kể từ khi nhận hàng nếu:</P>
              <UL items={[
                "chưa sử dụng;",
                "chưa mở hoặc còn nguyên bao bì gốc;",
                "ở tình trạng nguyên vẹn, có thể bán lại;",
                "chưa được kích hoạt, mã hóa, cá nhân hóa, trầy xước, hư hỏng hoặc thay đổi.",
              ]} />
              <P>Khách hàng chịu chi phí vận chuyển trả hàng, trừ khi sản phẩm bị lỗi hoặc chúng tôi giao sai sản phẩm.</P>
              <P>Sau khi kiểm tra và phê duyệt hàng trả lại, Banana Cards có thể hoàn tiền, thay thế hoặc cấp tín dụng cửa hàng.</P>
            </>
          ),
        },
        {
          id: "custom",
          title: "2. Thẻ tùy chỉnh, cá nhân hóa, in ấn hoặc mã hóa",
          body: (
            <>
              <P>Thẻ tùy chỉnh, thẻ cá nhân hóa, thẻ in, thẻ khắc, thẻ NFC đã mã hóa, thẻ liên kết QR, thẻ mang thương hiệu công ty, thẻ Google Review, và bất kỳ thẻ nào đã được kích hoạt hoặc gán vào hồ sơ khách hàng <strong>không đủ điều kiện đổi trả hoặc hoàn tiền</strong>, trừ khi sản phẩm bị lỗi hoặc do Banana Cards sản xuất sai.</P>
              <P>Điều này do các sản phẩm này được sản xuất hoặc cấu hình riêng cho khách hàng và không thể bán lại.</P>
            </>
          ),
        },
        {
          id: "faulty",
          title: "3. Sản phẩm lỗi hoặc sai",
          body: (
            <>
              <P>Nếu sản phẩm đến trong tình trạng lỗi, hư hỏng, in sai, mã hóa sai hoặc khác biệt đáng kể so với xác nhận đơn hàng, khách hàng phải liên hệ Banana Cards trong vòng 7 ngày kể từ khi nhận hàng.</P>
              <P>Khách hàng có thể được yêu cầu cung cấp:</P>
              <UL items={[
                "mã đơn hàng;",
                "ảnh hoặc video về vấn đề;",
                "mô tả sự cố;",
                "bằng chứng giao hàng hoặc mua hàng.",
              ]} />
              <P>Nếu sự cố được xác nhận do Banana Cards gây ra, chúng tôi sẽ đề xuất một trong các phương án sau:</P>
              <UL items={[
                "thay thế;",
                "sửa chữa hoặc mã hóa lại;",
                "tín dụng cửa hàng;",
                "hoàn tiền nếu không thể thay thế hoặc khắc phục.",
              ]} />
            </>
          ),
        },
        {
          id: "nfc",
          title: "4. Chức năng NFC",
          body: (
            <>
              <P>Banana Cards được thiết kế để hoạt động với các smartphone và thiết bị hỗ trợ NFC tương thích. Hiệu suất NFC có thể khác nhau tùy theo mẫu máy, ốp lưng, cài đặt thiết bị, trình duyệt, hệ điều hành hoặc hành vi ứng dụng bên thứ ba.</P>
              <P>Thẻ sẽ không được coi là lỗi nếu:</P>
              <UL items={[
                "điện thoại của khách hàng không hỗ trợ NFC;",
                "NFC bị tắt trên thiết bị của khách hàng;",
                "thẻ bị chặn bởi ốp lưng kim loại hoặc phụ kiện;",
                "sự cố do cài đặt sai, liên kết sai hoặc cấu hình phía khách hàng;",
                "thẻ hoạt động bình thường khi thử trên thiết bị tương thích khác.",
              ]} />
              <P>Khi có thể, Banana Cards sẽ hỗ trợ khắc phục sự cố, cấu hình lại hoặc mã hóa lại.</P>
            </>
          ),
        },
        {
          id: "subscriptions",
          title: "5. Gói đăng ký phần mềm",
          body: (
            <>
              <P>Gói đăng ký phần mềm Banana Cards được tính phí theo gói đã chọn.</P>
              <P>Phí đăng ký thường không được hoàn lại sau khi chu kỳ thanh toán đã bắt đầu, trừ khi pháp luật yêu cầu hoặc Banana Cards xác nhận sự cố kỹ thuật ngăn cản truy cập dịch vụ và không thể khắc phục.</P>
              <P>Khách hàng có thể hủy đăng ký trước chu kỳ thanh toán tiếp theo để tránh các khoản phí trong tương lai. Việc hủy không tự động hoàn tiền các kỳ đã thanh toán trước đó.</P>
            </>
          ),
        },
        {
          id: "google-review",
          title: "6. Thẻ Google Review",
          body: (
            <>
              <P>Thẻ Google Review được cấu hình bằng liên kết đánh giá từ Hồ sơ Doanh nghiệp Google của khách hàng.</P>
              <P>Banana Cards chịu trách nhiệm về phần cứng thẻ và cấu hình NFC/QR, nhưng không chịu trách nhiệm về:</P>
              <UL items={[
                "các thay đổi do Google thực hiện;",
                "việc đình chỉ hoặc xóa Hồ sơ Doanh nghiệp Google;",
                "liên kết sai do khách hàng cung cấp;",
                "đánh giá không được Google đăng tải;",
                "hành vi đánh giá của khách hàng;",
                "sự cố tài khoản Google, quyền hoặc niêm yết doanh nghiệp.",
              ]} />
              <P>Nếu khách hàng cung cấp liên kết sai, Banana Cards có thể hỗ trợ cập nhật thẻ khi khả thi về mặt kỹ thuật.</P>
            </>
          ),
        },
        {
          id: "refund-processing",
          title: "7. Xử lý hoàn tiền",
          body: (
            <>
              <P>Các khoản hoàn tiền được phê duyệt sẽ được xử lý qua phương thức thanh toán ban đầu khi có thể.</P>
              <P>Thời gian hoàn tiền có thể khác nhau tùy thuộc vào nhà cung cấp thanh toán, ngân hàng hoặc đơn vị phát hành thẻ.</P>
              <P>Phí vận chuyển, phí giao dịch, phí tùy chỉnh, phí thiết kế và dịch vụ cài đặt đã hoàn tất có thể không được hoàn lại trừ khi việc trả hàng do lỗi của Banana Cards.</P>
            </>
          ),
        },
        {
          id: "return-shipping",
          title: "8. Vận chuyển hàng trả",
          body: (
            <>
              <P>Trừ khi sản phẩm bị lỗi hoặc Banana Cards giao sai sản phẩm, khách hàng chịu trách nhiệm chi phí vận chuyển hàng trả.</P>
              <P>Hàng trả phải được đóng gói cẩn thận. Banana Cards không chịu trách nhiệm với hàng bị thất lạc hoặc hư hỏng trong quá trình vận chuyển trả về.</P>
            </>
          ),
        },
        {
          id: "not-eligible",
          title: "9. Sản phẩm không đủ điều kiện đổi trả",
          body: (
            <>
              <P>Các sản phẩm sau không đủ điều kiện đổi trả trừ khi bị lỗi:</P>
              <UL items={[
                "thẻ in tùy chỉnh;",
                "thẻ kim loại khắc;",
                "thẻ mang thương hiệu công ty;",
                "thẻ NFC đã kích hoạt;",
                "thẻ đã mã hóa hoặc liên kết hồ sơ;",
                "thẻ đã sử dụng hoặc hư hỏng;",
                "thẻ khuyến mãi, mẫu, hoặc tặng;",
                "gói đăng ký phần mềm đã sử dụng trong kỳ thanh toán;",
                "dịch vụ thiết kế, cài đặt, onboarding hoặc cấu hình đã hoàn tất.",
              ]} />
            </>
          ),
        },
        {
          id: "how-to-request",
          title: "10. Cách yêu cầu đổi trả hoặc hoàn tiền",
          body: (
            <>
              <P>Để yêu cầu hỗ trợ, khách hàng vui lòng liên hệ Banana Cards kèm:</P>
              <UL items={[
                "họ và tên đầy đủ;",
                "mã đơn hàng;",
                "sản phẩm đã mua;",
                "lý do yêu cầu đổi trả hoặc hoàn tiền;",
                "ảnh hoặc video nếu có.",
              ]} />
              <P>
                Gửi email tới{" "}
                <a href="mailto:hello@banana.cards" className="font-bold underline-offset-4 hover:underline">hello@banana.cards</a>
                {" "}và đội ngũ của chúng tôi sẽ xem xét yêu cầu và xác nhận các bước tiếp theo.
              </P>
            </>
          ),
        },
      ]}
    />
  );
}